import React from 'react';
import {
  FieldContainer,
  SelectOptionElement,
  useDidUpdateEffect,
} from '@odin-labs/components';
import {
  ChangeType,
  JobsiteFormSessionAssignment,
  JobsiteFormAutoCreateTrigger,
} from 'apollo/generated/client-operations';
import { getSelectOptionsFromStringEnum } from 'utils';
import { ExclamationTriangleIcon } from 'components/icons';
import {
  FormInputTypes,
  GridColSpan,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
} from 'components/form';
import {
  EditJobsiteFormFormData,
  FormOptionElement,
} from 'containers/jobsiteConfiguration/types';
import { EditJobsiteFormInputArgs } from './types';

export const sessionAssignmentOptions = getSelectOptionsFromStringEnum(
  JobsiteFormSessionAssignment,
);

// Human-friendly labels for the auto-assignment trigger dropdown. The raw enum values
// (following_exit / morning_after_any_activity) are not presentable, so options are defined
// explicitly rather than derived from the enum.
export const autoCreateTriggerOptions: SelectOptionElement<JobsiteFormAutoCreateTrigger>[] =
  [
    {
      value: JobsiteFormAutoCreateTrigger.FollowingExit,
      label: "Following the worker's exit",
    },
    {
      value: JobsiteFormAutoCreateTrigger.MorningAfterAnyActivity,
      label: 'Morning after any activity',
    },
  ];

const autoAssignWarningBodyByTrigger: Record<
  JobsiteFormAutoCreateTrigger,
  string
> = {
  [JobsiteFormAutoCreateTrigger.FollowingExit]:
    'The log is automatically created and assigned to the worker after their first exit from the jobsite on a given day.',
  [JobsiteFormAutoCreateTrigger.MorningAfterAnyActivity]:
    'The log is automatically created and assigned to the worker the next morning after having any jobsite activity the prior day.',
};

const getAutoAssignWarning = (
  trigger: JobsiteFormAutoCreateTrigger | null,
): { header: React.ReactNode; body: string; key: string } | null => {
  if (!trigger) return null;
  return {
    header: (
      <>
        <ExclamationTriangleIcon className="odin-text-amber-500" />
        &nbsp;Auto-assignment enabled
      </>
    ),
    body: autoAssignWarningBodyByTrigger[trigger],
    key: 'auto-gen-warning',
  };
};

type GetFormInputsArgs = EditJobsiteFormInputArgs & {
  selectedForm?: FormOptionElement | null;
  autoCreateTrigger?: SelectOptionElement<JobsiteFormAutoCreateTrigger> | null;
};

export const getFormInputs = (
  args: GetFormInputsArgs,
): TypedFormInputs<EditJobsiteFormFormData> => {
  const {
    formsOptions,
    jobsiteForms,
    jobsiteForm,
    selectedForm,
    autoCreateTrigger,
  } = args;

  // The form selection is locked once the entry has been persisted; only newly
  // added (still pending save) entries can have their form changed.
  const isFormSelectDisabled =
    !!jobsiteForm && jobsiteForm.changeType !== ChangeType.Created;

  // Hide forms that are already assigned via another (non-removed) entry, so
  // the same log can't be picked twice for a single jobsite.
  const availableFormsOptions = formsOptions.filter((opt) => {
    const isSelectedByOtherEntry = jobsiteForms?.some(
      (jf) =>
        jf.id !== jobsiteForm?.id &&
        jf.changeType !== ChangeType.Removed &&
        jf.form?.value === opt.value,
    );
    return !isSelectedByOtherEntry;
  });

  const warning = getAutoAssignWarning(autoCreateTrigger?.value ?? null);

  return {
    form: {
      element: FormInputTypes.Select,
      label: 'Log',
      layout: [GridColSpan.SpanFull],
      elementProps: {
        clearToNull: true,
        isClearable: false,
        options: availableFormsOptions,
        disabled: isFormSelectDisabled,
      },
      validation: { required: true },
    },
    logDescription: {
      element: FormInputTypes.CustomContent,
      elementProps: {
        content: (
          <FieldContainer name="description" label="About this log">
            <p className="odin-text-sm odin-text-gray-700 odin-leading-relaxed">
              {selectedForm?.description ??
                'This log does not have a description.'}
            </p>
          </FieldContainer>
        ),
      },
      layout: selectedForm ? GridColSpan.SpanFull : 'odin-hidden',
    },
    sessionAssignment: {
      element: FormInputTypes.Select,
      label: 'Session Assignment',
      layout: selectedForm?.allowSessionAssignment
        ? GridColSpan.SpanFull
        : 'odin-hidden',
      elementProps: {
        clearToNull: true,
        options: sessionAssignmentOptions,
        disabled: !selectedForm?.allowSessionAssignment,
      },
    },
    autoCreateTrigger: {
      element: FormInputTypes.Select,
      label: 'Auto-assignment',
      layout: selectedForm?.allowAutoCreateOnCheckout
        ? GridColSpan.SpanFull
        : 'odin-hidden',
      elementProps: {
        clearToNull: true,
        options: autoCreateTriggerOptions,
        disabled: !selectedForm?.allowAutoCreateOnCheckout,
      },
    },
    alert: {
      element: FormInputTypes.CustomContent,
      elementProps: {
        content: warning ? (
          <FieldContainer
            name="description"
            label={warning.header}
            key={warning.key}
            className="odin-rounded-md odin-border odin-border-amber-500 odin-bg-amber-50 odin-p-4"
          >
            <p className="odin-text-sm odin-text-gray-700 odin-leading-relaxed">
              {warning.body}
            </p>
          </FieldContainer>
        ) : null,
      },
      layout: warning ? GridColSpan.SpanFull : 'odin-hidden',
    },
  };
};

export const getFormInputsHook =
  (args: EditJobsiteFormInputArgs): UseInputs<EditJobsiteFormFormData> =>
  ({
    watch,
    setValue,
    getValues,
  }: UseFormMethods<EditJobsiteFormFormData>): TypedFormInputs<EditJobsiteFormFormData> => {
    const { formsOptions, jobsiteForms, jobsiteForm } = args;
    const selectedForm = watch('form') as FormOptionElement | null;
    const autoCreateTrigger = watch(
      'autoCreateTrigger',
    ) as SelectOptionElement<JobsiteFormAutoCreateTrigger> | null;

    // When the user switches to a form that doesn't support a given setting, reset that field so
    // a stale value isn't silently submitted.
    useDidUpdateEffect(() => {
      const {
        sessionAssignment: currentSessionAssignment,
        autoCreateTrigger: currentAutoCreateTrigger,
      } = getValues() ?? {};
      if (currentSessionAssignment && !selectedForm?.allowSessionAssignment) {
        setValue('sessionAssignment', null, { shouldDirty: true });
      }
      if (
        currentAutoCreateTrigger &&
        !selectedForm?.allowAutoCreateOnCheckout
      ) {
        setValue('autoCreateTrigger', null, { shouldDirty: true });
      }
    }, [selectedForm]);

    return React.useMemo(
      () =>
        getFormInputs({
          formsOptions,
          jobsiteForms,
          jobsiteForm,
          selectedForm,
          autoCreateTrigger,
        }),
      [
        formsOptions,
        jobsiteForms,
        jobsiteForm,
        selectedForm,
        autoCreateTrigger,
      ],
    );
  };

export const getDefaultValues = (
  jobsiteForm?: EditJobsiteFormFormData,
): EditJobsiteFormFormData => {
  const { form, sessionAssignment, autoCreateTrigger } = jobsiteForm ?? {};
  return {
    form: form ?? null,
    sessionAssignment: sessionAssignment ?? null,
    autoCreateTrigger: autoCreateTrigger ?? null,
    alert: null,
    logDescription: null,
  };
};
